import React, { useState, useEffect, ReactNode, JSX, useCallback, useRef } from "react";
import useCrmApi from "../../../components/hook/crm-req";
import { Conversation, InsightTab, MobileView } from "./types";
import {
  useWebSocketContext,
  Message as WSMessage,
  Conversation as WSConversation,
} from "../../../components/crm/socket-context";
import { TicketProps } from "../ticket/types";
import { ContactItem } from "../customers/types";
import { TeamDetails } from "../../../components/crm/customer/types";
import useRequest from "../../../components/hook/use-req";
import {
  getConversationUnreadCount,
  getFirstContactFromResponse,
  getFirstNameSearchTerm,
  getFirstTicketFromResponse,
  getRealtimeConversationId,
  Icons,
  isInboundDirection,
  isSameConversationId,
  normalizeConversation,
} from "./constant";
import { Avatar } from "../../../components/crm/conversation/components/avatar";
import { Sidebar } from "../../../components/crm/conversation/components/sidebar";
import { ChatArea } from "../../../components/crm/conversation/components/chatarea";
import { InsightsPanel } from "../../../components/crm/conversation/components/insight";
import { FormPreviewPanel } from "../../../components/crm/conversation/components/form-preview-panel";
import { EmailEditorDialog } from "../../../components/crm/conversation/components/email-editor-dialog";
import { LinkedInChannel } from "../../../components/crm/conversation/channel/linkedin-channel";
import { EmailChannel } from "../../../components/crm/conversation/channel/email-channel";

// ─── ChannelTab ───────────────────────────────────────────────────────────────
interface ChannelTabProps {
  label: string;
  count?: number;
  icon: ReactNode;
  active: boolean;
  onClick: () => void;
}

const ChannelTab: React.FC<ChannelTabProps> = ({
  label,
  count,
  icon,
  active,
  onClick,
}) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1 px-3 py-3 rounded-lg font-semibold text-xs whitespace-nowrap ${active
      ? "bg-[#9ED8B5] text-[#1C6E46] border-none dark:bg-emerald-500/20 dark:text-emerald-400"
      : "bg-white text-[#364F61] border border-slate-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700"
      }`}
  >
    {icon}
    <span>{label}</span>
    {count !== undefined && (
      <span
        className={`rounded-full px-[6px] py-px text-[11px] ${active ? "bg-white/30" : "bg-slate-200 dark:bg-gray-700 dark:text-gray-300"
          }`}
      >
        {count}
      </span>
    )}
  </button>
);

type ConversationTarget = {
  conversationId?: string;
  phoneNumber?: string;
  title?: string;
  // Which conversation list to look in — set by callers (e.g. the broadcast
  // report's "View Chat") that already know the source channel. Missing for
  // older callers, which just fall back to the WhatsApp list.
  channel?: string;
};

const digitsOnly = (value?: string | null): string =>
  String(value || "").replace(/\D/g, "");

const matchesConversationTarget = (
  conv: Conversation,
  target: ConversationTarget,
): boolean => {
  if (target.conversationId && conv.id === target.conversationId) return true;

  const targetPhone = digitsOnly(target.phoneNumber);
  if (targetPhone) {
    const threadPhone = digitsOnly(conv.external_thread_id);
    const titlePhone = digitsOnly(conv.title);
    const matchesThreadPhone = !!threadPhone && (
      threadPhone === targetPhone ||
      threadPhone.endsWith(targetPhone) ||
      targetPhone.endsWith(threadPhone)
    );
    const matchesTitlePhone = !!titlePhone && titlePhone.includes(targetPhone);
    return matchesThreadPhone || matchesTitlePhone;
  }

  if (target.title) {
    return conv.title?.toLowerCase() === target.title.toLowerCase();
  }

  return false;
};

// ─── App Root ─────────────────────────────────────────────────────────────────
export default function ConversationDashboard(): JSX.Element {
  const [isNewConversation, setIsNewConversation] = useState<boolean>(false);
  const [selectedConv, setSelectedConv] = useState<Conversation | null>(null);
  const [activeChannel, setActiveChannel] = useState<number>(1);
  const [activeListTab, setActiveListTab] = useState<number>(0);
  const [activeInsightTab, setActiveInsightTab] =
    useState<InsightTab>("Actions");
  const [showInsights, setShowInsights] = useState<boolean>(false);
  // Swaps the right panel to the static Form Preview (see avatar.tsx's new
  // "Open form" button on a Flow message) in place of ConvoAI Insights —
  // mutually exclusive with it, not stacked.
  const [showFormPreview, setShowFormPreview] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [mobileView, setMobileView] = useState<MobileView>("list");
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  // The backend's GET /conversations?limit=N returns a flat array of the N
  // most recent conversations — no cursor/nextCursor, no {items, ...}
  // wrapper. So "pagination" here is just re-requesting with a bigger
  // `limit`: "Load older" bumps limit by 10 and refetches; "Load previous"
  // drops it by 10 and re-slices the last full fetch (no network call).
  const [conversationsLimit, setConversationsLimit] = useState<number>(10);
  const fullConversationsRef = useRef<Conversation[]>([]); // last network fetch, at conversationsLimit
  const reachedEndRef = useRef<boolean>(false); // last fetch returned fewer than requested -> no more to load
  const [loadingMoreConversations, setLoadingMoreConversations] = useState<boolean>(false);
  const [leadContact, setLeadContact] = useState<ContactItem | null>(null);
  const [assignedTicket, setAssignedTicket] = useState<TicketProps | null>(
    null,
  );
  const [team, setTeam] = useState<TeamDetails[]>([]);
  const [leadLoading, setLeadLoading] = useState<boolean>(false);
  const [isEmailEditorOpen, setIsEmailEditorOpen] = useState<boolean>(false);
  const [pendingConversationTarget, setPendingConversationTarget] =
    useState<ConversationTarget | null>(null);
  // Prefills the "new conversation" recipient field when a deep-link (e.g.
  // from the broadcast report or Leads) finds no existing conversation for
  // that phone number, so the user lands on a ready-to-send compose instead
  // of nothing happening.
  const [newConversationRecipient, setNewConversationRecipient] = useState<string | null>(null);
  const token = localStorage.getItem("authToken");
  const [direction, setDirection] = useState<"in" | "out">("in");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const selectedConvRef = useRef<Conversation | null>(null);
  // Get WebSocket context
  const ws = useWebSocketContext();

  const { makeRequest: getConversationList } = useCrmApi(
    `/conversations`,
    "GET",
    {
      Authorization: `Bearer ${token}`,
    },
  );

  const { makeRequest: getSelectedContact } = useCrmApi(`/contacts`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: getSelectedTicket } = useCrmApi(
    `/tickets?conversation_id=${selectedConv?.id || ""}`,
    "GET",
    { Authorization: `Bearer ${token}` },
  );

  const { makeRequest: getTeamList } = useRequest(`/profile/users`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  const [rcsConversations, setRcsConversations] = useState<Conversation[]>([]);
  const [rcsLoading, setRcsLoading] = useState<boolean>(false);
  // Website chat widget conversations (channel=widget, widget-integration.md).
  const [widgetConversations, setWidgetConversations] = useState<Conversation[]>([]);
  const [widgetLoading, setWidgetLoading] = useState<boolean>(false);

  const whatsappCount = conversations.length;

  const handleChannelClick = (index: number) => {
    // Clear selected conversation when switching channels to avoid stale data
    setSelectedConv(null);
    selectedConvRef.current = null;
    setActiveChannel(index);
  };

  const channelConfigs = [
    {
      label: "All Channels",
      count: whatsappCount,
      icon: <span className="text-xl">⊞</span>,
    },
    {
      label: "WhatsApp",
      count: whatsappCount,
      icon: <Icons.Whatsapp />,
    },
    {
      label: "SMS",
      count: 0,
      icon: <Icons.SMS />,
    },
    {
      label: "Email",
      count: 0,
      icon: <Icons.Email />,
    },
    {
      label: "RCS",
      count: rcsConversations.length,
      icon: <Icons.RCS />,
    },
    {
      label: "LinkedIn",
      count: 0,
      icon: <Icons.Linkedin />,
    },
    {
      label: "Voice",
      count: 0,
      icon: <Icons.Voice />,
    },
    // Support Agent website-widget chats (GET /conversations?channel=widget).
    // chatarea.tsx identifies tabs by index (RCS = 4, Agents = 7).
    {
      label: "Agents",
      count: widgetConversations.length,
      icon: <Icons.Agents />,
    },
  ];

  const activeChannelConfig = channelConfigs[activeChannel];
const isLinkedInChannel =
  activeChannelConfig?.label === "LinkedIn" &&
  false;
  const isEmailChannel = activeChannelConfig?.label === "Email";
  const isRcsChannel = activeChannelConfig?.label === "RCS";
  const isWidgetChannel = activeChannelConfig?.label === "Agents";
  const isComingSoonChannel =
    activeChannelConfig &&
    activeChannelConfig.label !== "All Channels" &&
    activeChannelConfig.label !== "WhatsApp" &&
    // activeChannelConfig.label !== "LinkedIn" &&
    activeChannelConfig.label !== "Email" &&
    activeChannelConfig.label !== "RCS" &&
    activeChannelConfig.label !== "Agents";

  const CONVERSATIONS_STEP = 10;

  // Backend examples for this contract used camelCase; every other endpoint
  // in this app has used snake_case. Tolerate both instead of guessing.
  const adaptConversationRecord = (c: any): Conversation => ({
    ...c,
    last_message_at: c.last_message_at ?? c.lastMessageAt ?? c.last_message?.created_at,
    contact_id: c.contact_id ?? c.contactId,
    unread_count: c.unread_count ?? c.unreadCount,
    last_message: c.last_message ?? c.lastMessage,
    external_thread_id: c.external_thread_id ?? c.externalThreadId,
    created_at: c.created_at ?? c.createdAt,
  });

  // Actual network fetch at a given `limit`. Backend returns a flat array of
  // the N most recent conversations (no cursor) — always replaces state.
  // This fetches WhatsApp conversations specifically with channel=whatsapp
  const fetchConversationsAtLimit = useCallback(
    async (limit: number, opts?: { direction?: string; search?: string; silent?: boolean }) => {
      if (opts?.silent) {
        setLoadingMoreConversations(true);
      } else {
        setLoading(true);
      }
      try {
        const params = new URLSearchParams();
        const dir = opts?.direction ?? direction;
        const search = opts?.search ?? searchQuery;
        params.set("channel", "whatsapp"); // Filter for WhatsApp conversations only
        if (dir) params.set("direction", dir);
        if (search) params.set("search", search);
        params.set("limit", String(limit));

        // Override the endpoint dynamically by appending query params.
        // Because useCrmApi locks in the path at hook initialisation,
        // we call getConversationList with a custom query-string override
        // passed as the second argument (params object).
        const [response] = await getConversationList(undefined, Object.fromEntries(params));
        let convs: Conversation[] = [];

        if (response && Array.isArray(response)) {
          convs = response;
        } else if (response && Array.isArray(response.items)) {
          convs = response.items;
        } else if (response && response.data && Array.isArray(response.data)) {
          convs = response.data;
        } else if (
          response &&
          response.conversations &&
          Array.isArray(response.conversations)
        ) {
          convs = response.conversations;
        }

        const storedUnread = JSON.parse(
          localStorage.getItem("ws_unread_counts") || "{}",
        );
        const normalizedConvs = convs.map((raw) => {
          const c = adaptConversationRecord(raw);
          return normalizeConversation({
            ...c,
            unread_count: storedUnread[c.id] ?? c.unread_count ?? 0,
          });
        });
        normalizedConvs.sort(
          (a, b) =>
            new Date(b.last_message_at).getTime() -
            new Date(a.last_message_at).getTime(),
        );

        fullConversationsRef.current = normalizedConvs;
        reachedEndRef.current = normalizedConvs.length < limit;
        setConversations(normalizedConvs);
        setConversationsLimit(limit);

        if (normalizedConvs.length > 0 && !selectedConvRef.current) {
          selectedConvRef.current = normalizedConvs[0];
          setSelectedConv(normalizedConvs[0]);
        }
        return normalizedConvs;
      } catch (error) {
        return [];
      } finally {
        setLoading(false);
        setLoadingMoreConversations(false);
      }
    },
    [direction, searchQuery],
  );

  const fetchConversations = useCallback(
    (opts?: { direction?: string; search?: string }) =>
      fetchConversationsAtLimit(CONVERSATIONS_STEP, opts),
    [fetchConversationsAtLimit],
  );

  // "Load older conversations" — bumps the limit and refetches (10 → 20 → 30…)
  const handleLoadMoreConversations = useCallback(() => {
    if (loadingMoreConversations || reachedEndRef.current) return;
    fetchConversationsAtLimit(conversationsLimit + CONVERSATIONS_STEP, { silent: true });
  }, [conversationsLimit, loadingMoreConversations, fetchConversationsAtLimit]);

  // "Load previous conversations" — drops the limit and re-slices the last
  // full fetch (30 → 20 → 10), no network call.
  const handleLoadPreviousConversations = useCallback(() => {
    if (conversationsLimit <= CONVERSATIONS_STEP) return;
    const newLimit = conversationsLimit - CONVERSATIONS_STEP;
    setConversations(fullConversationsRef.current.slice(0, newLimit));
    setConversationsLimit(newLimit);
  }, [conversationsLimit]);

  const hasMoreConversations = !reachedEndRef.current;
  const hasPreviousConversations = conversationsLimit > CONVERSATIONS_STEP;

  // Fetch RCS (channel=rcs) or website widget (channel=widget) conversations
  const fetchRcsConversations = useCallback(async (channel: "rcs" | "widget" = "rcs") => {
    const setChannelLoading = channel === "widget" ? setWidgetLoading : setRcsLoading;
    setChannelLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("channel", channel);
      params.set("limit", String(CONVERSATIONS_STEP));
      if (direction) params.set("direction", direction);
      if (searchQuery) params.set("search", searchQuery);

      const [response] = await getConversationList(undefined, Object.fromEntries(params));

      let convs: Conversation[] = [];
      if (response && Array.isArray(response)) {
        convs = response;
      } else if (response && Array.isArray(response.items)) {
        convs = response.items;
      } else if (response && response.data && Array.isArray(response.data)) {
        convs = response.data;
      } else if (response && response.conversations && Array.isArray(response.conversations)) {
        convs = response.conversations;
      }

      const storedUnread = JSON.parse(localStorage.getItem("ws_unread_counts") || "{}");
      const normalizedConvs = convs.map((raw) => {
        const c = adaptConversationRecord(raw);
        return normalizeConversation({
          ...c,
          unread_count: storedUnread[c.id] ?? c.unread_count ?? 0,
        });
      });
      normalizedConvs.sort(
        (a, b) => new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
      );

      (channel === "widget" ? setWidgetConversations : setRcsConversations)(normalizedConvs);

      // Auto-select first conversation if none selected
      if (normalizedConvs.length > 0 && !selectedConvRef.current) {
        selectedConvRef.current = normalizedConvs[0];
        setSelectedConv(normalizedConvs[0]);
      }
    } catch (error) {
      console.error(`Error fetching ${channel} conversations:`, error);
    } finally {
      setChannelLoading(false);
    }
  }, [direction, searchQuery]);

  useEffect(() => {
    selectedConvRef.current = selectedConv;
  }, [selectedConv]);

  useEffect(() => {
    fetchConversations({ direction, search: searchQuery });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction]);

  // Fetch RCS conversations when RCS channel is selected
  useEffect(() => {
    if (isRcsChannel) {
      fetchRcsConversations("rcs");
    }
  }, [isRcsChannel, fetchRcsConversations]);

  useEffect(() => {
    if (isWidgetChannel) {
      fetchRcsConversations("widget");
    }
  }, [isWidgetChannel, fetchRcsConversations]);

  // ─── Debounced re-fetch when search query changes ────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchConversations({ direction, search: searchQuery });
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);
  // ─── Re-fetch when direction changes ─────────────────────────────────────────




  useEffect(() => {
    const handleUnreadUpdate = (
      e: CustomEvent<{
        conversationId: string;
        counts: Record<string, number>;
      }>,
    ) => {
      setConversations((prev) =>
        prev.map((c) => ({
          ...c,
          unread_count:
            c.id === e.detail.conversationId && selectedConv?.id !== c.id
              ? (e.detail.counts[c.id] ?? c.unread_count)
              : c.unread_count,
        })),
      );
    };
    window.addEventListener("ws-unread-updated" as any, handleUnreadUpdate);
    return () =>
      window.removeEventListener(
        "ws-unread-updated" as any,
        handleUnreadUpdate,
      );
  }, [selectedConv?.id]);

  function handleNewConversation() {
    setIsNewConversation(true);
    setSelectedConv(null);
    setNewConversationRecipient(null);
    if (isMobile) setMobileView("chat");
  }


  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const [response] = await getTeamList();
        if (Array?.isArray(response)) {
          setTeam(response);
        } else if (Array?.isArray(response?.users)) {
          setTeam(response?.users);
        } else if (Array.isArray(response?.results)) {
          setTeam(response.results);
        }
      } catch {
        setTeam([]);
      }
    };

    fetchTeam();
  }, []);

  useEffect(() => {
    const fetchSelectedLeadDetails = async () => {
      if (!selectedConv) {
        setLeadContact(null);
        setAssignedTicket(null);
        return;
      }

      // Prefer the conversation's linked contact_id — an exact lookup —
      // over the old fuzzy first-name/title search, which mismatched for
      // some conversations. Fall back to the name search only when a
      // conversation has no linked contact yet.
      const contactId = selectedConv.contact_id;
      const search = getFirstNameSearchTerm(
        selectedConv.title || selectedConv.external_thread_id || "",
      );
      setLeadLoading(true);

      try {
        const [contactResponse] = contactId
          ? await getSelectedContact(undefined, undefined, contactId)
          : search
            ? await getSelectedContact(undefined, { search })
            : [null];
        setLeadContact(getFirstContactFromResponse(contactResponse));

        const [ticketResponse] = await getSelectedTicket();
        setAssignedTicket(getFirstTicketFromResponse(ticketResponse));
      } catch (error) {
        setLeadContact(null);
        setAssignedTicket(null);
      } finally {
        setLeadLoading(false);
      }
    };

    fetchSelectedLeadDetails();
  }, [selectedConv?.id, selectedConv?.contact_id, selectedConv?.title, selectedConv?.external_thread_id]);

  useEffect(() => {
    // Below lg (1024px) the 3-fixed-width-column desktop layout (260px list +
    // flexible chat + 280px insights) no longer has room to breathe — switch
    // to the existing single-pane mobile nav earlier instead of only at true
    // phone widths, so tablets/small laptop windows get it too.
    const check = (): void => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const handleSelectConv = (conv: Conversation): void => {
    setIsNewConversation(false);
    setNewConversationRecipient(null);
    // Clear unread count in localStorage
    const storedUnread = JSON.parse(
      localStorage.getItem("ws_unread_counts") || "{}",
    );
    delete storedUnread[conv.id];
    localStorage.setItem("ws_unread_counts", JSON.stringify(storedUnread));

    const openedConversation = { ...conv, unread_count: 0 };
    selectedConvRef.current = openedConversation;
    setSelectedConv(openedConversation);
    setConversations((prev) =>
      prev.map((item) =>
        item.id === conv.id ? { ...item, unread_count: 0 } : item,
      ),
    );
    if (isMobile) setMobileView("chat");
  };

  const openConversationTarget = useCallback(
    async (target: ConversationTarget) => {
      // RCS conversations live in a separate list/tab — switch there instead
      // of searching the WhatsApp list, which will never contain them.
      if (target.channel === "rcs") {
        const rcsIndex = channelConfigs.findIndex((c) => c.label === "RCS");
        if (rcsIndex >= 0) setActiveChannel(rcsIndex);

        const found = rcsConversations.find((conv) =>
          matchesConversationTarget(conv, target),
        );
        if (found) {
          setPendingConversationTarget(null);
          handleSelectConv(found);
          return;
        }

        setPendingConversationTarget(target);
        return;
      }

      const found = conversations.find((conv) =>
        matchesConversationTarget(conv, target),
      );

      if (found) {
        setPendingConversationTarget(null);
        handleSelectConv(found);
        return;
      }

      // Prefer an exact lookup by id (when the caller already knows it — the
      // broadcast report and Archive page both pass one) over a fuzzy
      // phone/title search against the paginated list, which can miss a
      // conversation that just isn't in the currently-loaded page.
      if (target.conversationId) {
        const [response, status] = await getConversationList(
          undefined,
          undefined,
          target.conversationId,
        );
        if (status >= 200 && status < 300 && response?.id) {
          const conv = normalizeConversation(adaptConversationRecord(response));
          setPendingConversationTarget(null);
          setConversations((prev) =>
            prev.some((c) => c.id === conv.id) ? prev : [conv, ...prev],
          );
          handleSelectConv(conv);
          return;
        }
      }

      setPendingConversationTarget(target);
      const search = target.phoneNumber || target.title || "";
      if (search) {
        setSearchQuery(search);
        const results = await fetchConversations({ direction: "", search });
        const matchAfterSearch = (results || []).find((conv) =>
          matchesConversationTarget(conv, target),
        );

        if (matchAfterSearch) {
          setPendingConversationTarget(null);
          handleSelectConv(matchAfterSearch);
          return;
        }
      }

      // No existing conversation for this person — clear whatever was
      // previously open and land on a fresh compose instead of silently
      // leaving an unrelated conversation on screen. Prefill the recipient
      // with the phone number if we have one (some callers only pass it as
      // `title` when there's no separate contact name).
      const looksLikePhoneNumber = /^\+?[0-9][0-9\s-]{5,}$/.test((search || "").trim());
      setPendingConversationTarget(null);
      setIsNewConversation(true);
      setSelectedConv(null);
      setNewConversationRecipient(target.phoneNumber || (looksLikePhoneNumber ? search : "") || "");
      if (isMobile) setMobileView("chat");
    },
    [conversations, rcsConversations, fetchConversations, getConversationList, isMobile],
  );

  useEffect(() => {
    if (!pendingConversationTarget) return;
    const isRcsTarget = pendingConversationTarget.channel === "rcs";
    const pool = isRcsTarget ? rcsConversations : conversations;
    if (pool.length === 0) return;

    const found = pool.find((conv) =>
      matchesConversationTarget(conv, pendingConversationTarget),
    );

    if (found) {
      setPendingConversationTarget(null);
      handleSelectConv(found);
    }
  }, [conversations, rcsConversations, pendingConversationTarget]);


  useEffect(() => {
    const handleNavigateByPhone = (event: CustomEvent<ConversationTarget>) => {
      openConversationTarget(event.detail || {});
    };

    window.addEventListener("navigate-to-conversation" as any, handleNavigateByPhone);
    window.addEventListener("select-conversation" as any, handleNavigateByPhone);

    return () => {
      window.removeEventListener("navigate-to-conversation" as any, handleNavigateByPhone);
      window.removeEventListener("select-conversation" as any, handleNavigateByPhone);
    };
  }, [openConversationTarget]);

  // ChatArea's "Archive Conversation" action lives outside this component's
  // props chain, so it notifies us via a window event instead — drop the
  // archived conversation from the active list and clear the selection if it
  // was the one open.
  useEffect(() => {
    const handleConversationArchived = (event: CustomEvent<{ conversationId: string }>) => {
      const { conversationId } = event.detail || ({} as { conversationId: string });
      if (!conversationId) return;
      setConversations((prev) => prev.filter((c) => c.id !== conversationId));
      if (selectedConvRef.current?.id === conversationId) {
        selectedConvRef.current = null;
        setSelectedConv(null);
      }
    };

    window.addEventListener("conversation-archived" as any, handleConversationArchived);
    return () => window.removeEventListener("conversation-archived" as any, handleConversationArchived);
  }, []);

  // Pick up a target left by another page (e.g. "Resume Conversation" from the
  // Archive screen) before navigating here — a CustomEvent alone wouldn't work
  // since this component isn't mounted yet when that click happens.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("pendingConversationTarget");
      if (raw) {
        sessionStorage.removeItem("pendingConversationTarget");
        openConversationTarget(JSON.parse(raw));
      }
    } catch {
      // malformed/unavailable sessionStorage — no-op, inbox just opens unfiltered.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConversationUpdate = (updatedConv: Conversation): void => {
    setConversations((prev) => {
      const exists = prev.some((c) => c.id === updatedConv.id);
      if (exists) {
        return prev.map((c) => (c.id === updatedConv.id ? updatedConv : c));
      } else {
        return [updatedConv, ...prev];
      }
    });
    selectedConvRef.current = updatedConv;
    setSelectedConv(updatedConv);
  };

  const handleMessageCreated = useCallback(
    (
      message: WSMessage,
      conversationId: string,
      conversation?: Conversation,
    ) => {
      const resolvedConversationId = getRealtimeConversationId(
        message as any,
        conversationId,
      );

      setConversations((prev) => {
        const exists = prev.some((conv) =>
          isSameConversationId(conv.id, resolvedConversationId),
        );

        const shouldIncrementUnread =
          isInboundDirection(message.direction) &&
          !isSameConversationId(selectedConv?.id, resolvedConversationId);

        let updated: Conversation[];

        if (exists) {
          updated = prev.map((conv) =>
            isSameConversationId(conv.id, resolvedConversationId)
              ? {
                ...conv,
                last_message_at: message.created_at,
                // ✅ FIXED: Proper LastMessage object
                last_message: {
                  id: message.id || `msg_${Date.now()}`,
                  direction: message.direction,
                  body: message.body || "",
                  type: message.type || "text",
                  status: message.status || "delivered",
                  created_at: message.created_at,
                },
                unread_count: shouldIncrementUnread
                  ? (conv.unread_count || 0) + 1
                  : isSameConversationId(selectedConv?.id, resolvedConversationId)
                    ? 0
                    : conv.unread_count || 0,
              }
              : conv,
          );
        } else if (conversation) {
          updated = [
            {
              ...normalizeConversation(conversation),
              last_message_at: message.created_at,

              last_message: {
                id: message.id || `msg_${Date.now()}`,
                direction: message.direction,
                body: message.body || "",
                type: message.type || "text",
                status: message.status || "delivered",
                created_at: message.created_at,
              },
              unread_count: shouldIncrementUnread ? 1 : 0,
            },
            ...prev,
          ];
        } else {
          updated = prev;
        }

        return updated.sort(
          (a, b) =>
            new Date(b.last_message_at).getTime() -
            new Date(a.last_message_at).getTime(),
        );
      });
    },
    [selectedConv?.id],
  );

  const handleMessageStatusUpdated = useCallback(
    (message: WSMessage, conversationId: string) => {
      window.dispatchEvent(
        new CustomEvent("ws-message-status-updated", {
          detail: { message, conversationId },
        }),
      );
    },
    [],
  );

  const handleConversationCreated = useCallback(
    (conversation: WSConversation) => {
      setConversations((prev) => {
        const exists = prev.some((c) => c.id === conversation.id);
        if (exists) return prev;
        const updated = [normalizeConversation(conversation), ...prev];
        return updated.sort(
          (a, b) =>
            new Date(b.last_message_at).getTime() -
            new Date(a.last_message_at).getTime(),
        );
      });
    },
    [],
  );

  const handleConversationUpdated = useCallback(
    (conversation: WSConversation) => {
      setConversations((prev) => {
        const updated = prev.map((c) =>
          c.id === conversation.id
            ? {
              ...normalizeConversation(conversation),
              unread_count:
                getConversationUnreadCount(conversation) ||
                c.unread_count ||
                0,
            }
            : c,
        );
        return updated.sort(
          (a, b) =>
            new Date(b.last_message_at).getTime() -
            new Date(a.last_message_at).getTime(),
        );
      });

      if (selectedConv?.id === conversation.id) {
        setSelectedConv(conversation);
      }
    },
    [selectedConv],
  );

  // Set up WebSocket event handlers
  useEffect(() => {
    ws.onMessageCreated = handleMessageCreated;
    ws.onMessageStatusUpdated = handleMessageStatusUpdated;
    ws.onConversationCreated = handleConversationCreated;
    ws.onConversationUpdated = handleConversationUpdated;
  }, [
    ws,
    handleMessageCreated,
    handleMessageStatusUpdated,
    handleConversationCreated,
    handleConversationUpdated,
  ]);

  useEffect(() => {
    const onMessageCreated = (
      event: CustomEvent<{
        message: WSMessage;
        conversationId: string;
        conversation?: Conversation;
      }>,
    ) => {
      handleMessageCreated(
        event.detail.message,
        event.detail.conversationId,
        event.detail.conversation,
      );
    };

    const onMessageStatusUpdated = (
      event: CustomEvent<{ message: WSMessage; conversationId: string }>,
    ) => {
      handleMessageStatusUpdated(
        event.detail.message,
        event.detail.conversationId,
      );
    };

    const onConversationCreated = (
      event: CustomEvent<{ conversation: WSConversation }>,
    ) => {
      handleConversationCreated(event.detail.conversation);
    };

    const onConversationUpdated = (
      event: CustomEvent<{ conversation: WSConversation }>,
    ) => {
      handleConversationUpdated(event.detail.conversation);
    };

    window.addEventListener(
      "ws-message-created" as any,
      onMessageCreated as EventListener,
    );
    window.addEventListener(
      "ws-message-status-updated" as any,
      onMessageStatusUpdated as EventListener,
    );
    window.addEventListener(
      "ws-conversation-created" as any,
      onConversationCreated as EventListener,
    );
    window.addEventListener(
      "ws-conversation-updated" as any,
      onConversationUpdated as EventListener,
    );

    return () => {
      window.removeEventListener(
        "ws-message-created" as any,
        onMessageCreated as EventListener,
      );
      window.removeEventListener(
        "ws-message-status-updated" as any,
        onMessageStatusUpdated as EventListener,
      );
      window.removeEventListener(
        "ws-conversation-created" as any,
        onConversationCreated as EventListener,
      );
      window.removeEventListener(
        "ws-conversation-updated" as any,
        onConversationUpdated as EventListener,
      );
    };
  }, [
    handleMessageCreated,
    handleMessageStatusUpdated,
    handleConversationCreated,
    handleConversationUpdated,
  ]);

  // Subscribe/Unsubscribe to conversation when selected changes
  useEffect(() => {
    if (ws.isConnected && selectedConv?.id) {
      ws.subscribe(selectedConv.id);
    }

    return () => {
      if (ws.isConnected && selectedConv?.id) {
        ws.unsubscribe(selectedConv.id);
      }
    };
  }, [selectedConv?.id, ws.isConnected, ws.subscribe, ws.unsubscribe]);

  // Refetch data on reconnect
  useEffect(() => {
    if (ws.isConnected && !ws.isReconnecting) {
      fetchConversations();
    }
  }, [ws.isConnected, ws.isReconnecting]);

  return (
    <div className="h-screen flex flex-col bg-[#FFFFFF] dark:bg-gray-900 rounded-xl shadow overflow-hidden">
      {/* Top Bar */}
      <div className="bg-white dark:bg-gray-900 border-b border-[#e8ecf4] dark:border-gray-800 px-5 py-2.5 flex items-center justify-between">
        <h1 className="font-extrabold text-base text-slate-800 dark:text-gray-100 m-0">
          All Conversations
        </h1>
        <div className="flex items-center gap-3">
          {/* <NotificationBell /> */}
          <button className="flex items-center gap-1.5 px-3.5 py-1.5 bg-slate-100 dark:bg-gray-800 rounded-lg border-none cursor-pointer font-bold text-sm text-slate-600 dark:text-gray-300">
            <Avatar
              src="https://i.pravatar.cc/30?img=1"
              initials="A"
              color="#6366f1"
              size={26}
            />
            Admin <Icons.ChevronDown size={14} />
          </button>
        </div>
      </div>

      {/* WebSocket Connection Status */}
      <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 bg-white dark:bg-gray-800 rounded-full shadow-lg px-3 py-1.5 border border-slate-200 dark:border-gray-700">
        <div
          className={`w-2 h-2 rounded-full ${ws.isConnected
            ? "bg-green-500 animate-pulse"
            : ws.isReconnecting
              ? "bg-yellow-500 animate-pulse"
              : "bg-red-500"
            }`}
        />
        <span className="text-xs font-medium text-slate-600 dark:text-gray-300">
          {ws.isConnected
            ? "Live Updates"
            : ws.isReconnecting
              ? "Reconnecting..."
              : "Offline"}
        </span>
        {!ws.isConnected && !ws.isReconnecting && (
          <button
            onClick={() => ws.connect()}
            className="ml-2 text-xs text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 font-semibold"
          >
            Reconnect
          </button>
        )}
      </div>

      {/* Channel Tabs */}
      <div className="bg-[#EAEDFB] dark:bg-gray-800/60 border-b border-[#e8ecf4] dark:border-gray-800 px-4 py-2 overflow-x-auto flex gap-1.5">
        {channelConfigs.map((ch, i) => (
          <ChannelTab
            key={i}
            label={ch.label}
            count={ch.count}
            icon={ch.icon}
            active={activeChannel === i}
            onClick={() => handleChannelClick(i)}
          />
        ))}
      </div>

      {/* Main Layout */}
      <div className="flex-1 flex overflow-hidden">
        {isLinkedInChannel ? (
          <LinkedInChannel />
        ) : isEmailChannel ? (
          <EmailChannel
            conversations={[]}
            loading={false}
            selectedConv={null}
            onSelectConv={() => { }}
            onNewConversation={() => { }}
            isNewConversation={false}
            activeListTab={0}
            onTabChange={() => { }}
            leadContact={leadContact}
            assignedTicket={assignedTicket}
            team={team}
            onContactUpdated={setLeadContact}
            onTicketSaved={setAssignedTicket}
            isMobile={isMobile}
            mobileView={mobileView}
            setMobileView={setMobileView}
            showInsights={showInsights}
            setShowInsights={setShowInsights}
            activeInsightTab={activeInsightTab}
            setActiveInsightTab={setActiveInsightTab}
          />
        ) : isRcsChannel || isWidgetChannel ? (
          <>
            {isMobile && mobileView === "chat" && (
              <div className="fixed top-[130px] left-2 z-[100]">
                <button
                  onClick={() => setMobileView("list")}
                  className="bg-indigo-500 text-white border-none rounded-lg px-3 py-1.5 text-xs font-bold cursor-pointer"
                >
                  ← Back
                </button>
              </div>
            )}

            {(!isMobile || mobileView === "list") && (
              <Sidebar
                selected={selectedConv}
                onSelect={handleSelectConv}
                activeTab={activeListTab}
                onTabChange={setActiveListTab}
                conversations={isWidgetChannel ? widgetConversations : rcsConversations}
                loading={isWidgetChannel ? widgetLoading : rcsLoading}
                onNewConversation={handleNewConversation}
                isNewConversation={isNewConversation}
                direction={direction}
                onDirectionChange={setDirection}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                hasMore={false}
                hasPrevious={false}
                loadingMore={false}
                onLoadMore={() => {}}
                onLoadPrevious={() => {}}
                isMobile={isMobile}
              />
            )}

            {(!isMobile || mobileView === "chat") && (
              <ChatArea
                conv={selectedConv}
                onOpenInsights={() => setShowInsights(true)}
                onOpenForm={() => setShowFormPreview(true)}
                isMobile={isMobile}
                onConversationUpdate={handleConversationUpdate}
                leadContact={leadContact}
                assignedTicket={assignedTicket}
                team={team}
                onContactUpdated={setLeadContact}
                onTicketSaved={setAssignedTicket}
                activeChannel={activeChannel}
                initialRecipient={newConversationRecipient}
              />
            )}

            {!isMobile && (showFormPreview ? (
              <FormPreviewPanel leadContact={leadContact} onClose={() => setShowFormPreview(false)} />
            ) : (
              <InsightsPanel
                activeInsightTab={activeInsightTab}
                onTabChange={setActiveInsightTab}
                leadContact={leadContact}
                assignedTicket={assignedTicket}
                loadingLead={leadLoading}
                team={team}
                conv={selectedConv}
              />
            ))}
          </>
        ) : isComingSoonChannel ? (
          <>
            <div className="w-[260px] min-w-[200px] bg-white dark:bg-gray-900 border-r border-[#e8ecf4] dark:border-gray-800 flex flex-col h-full overflow-hidden">
              <div className="p-3 pb-2 border-b border-slate-100 dark:border-gray-800 bg-[#EAEDFB] dark:bg-gray-800/60">
                <div className="flex items-center gap-2 bg-slate-50 dark:bg-gray-800 rounded-lg p-2 border border-slate-200 dark:border-gray-700">
                  <Icons.Search />
                  <input
                    disabled
                    placeholder={`Search ${activeChannelConfig.label}`}
                    className="border-none bg-transparent outline-none text-xs text-slate-400 dark:text-gray-500 w-full disabled:cursor-not-allowed"
                  />
                </div>
              </div>
              <div className="flex-1 flex items-center justify-center px-6 text-center">
                <div>
                  <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 dark:bg-gray-800 text-slate-400 dark:text-gray-500">
                    {activeChannelConfig.icon}
                  </div>
                  <p className="text-sm font-semibold text-slate-600 dark:text-gray-300">
                    No conversations yet
                  </p>
                  <p className="mt-1 text-xs text-slate-400 dark:text-gray-500">
                    {activeChannelConfig.label} is coming soon.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex-1 bg-[#f7f8fb] dark:bg-gray-950 flex items-center justify-center">
              <div className="text-center px-6">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 text-indigo-500 dark:text-indigo-400 shadow-sm">
                  {activeChannelConfig.icon}
                </div>
                <h2 className="text-xl font-bold text-slate-800 dark:text-gray-100">
                  {activeChannelConfig.label} Conversations
                </h2>
                <p className="mt-2 text-sm text-slate-500 dark:text-gray-400">
                  Coming soon. This channel will be available here when it
                  launches.
                </p>
                {activeChannelConfig.label === "Email" && (
                  <button
                    onClick={() => setIsEmailEditorOpen(true)}
                    className="mt-4 px-4 py-2 bg-indigo-500 text-white rounded-lg font-semibold text-sm hover:bg-indigo-600 transition-colors cursor-pointer border-none flex items-center gap-2 mx-auto"
                  >
                    <Icons.Email />
                    Init Email Editor
                  </button>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            {isMobile && mobileView === "chat" && (
              <div className="fixed top-[130px] left-2 z-[100]">
                <button
                  onClick={() => setMobileView("list")}
                  className="bg-indigo-500 text-white border-none rounded-lg px-3 py-1.5 text-xs font-bold cursor-pointer"
                >
                  ← Back
                </button>
              </div>
            )}

            {(!isMobile || mobileView === "list") && (
              <Sidebar
                selected={selectedConv}
                onSelect={handleSelectConv}
                activeTab={activeListTab}
                onTabChange={setActiveListTab}
                conversations={conversations}
                loading={loading}
                onNewConversation={handleNewConversation}
                isNewConversation={isNewConversation}
                direction={direction}
                onDirectionChange={setDirection}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                hasMore={hasMoreConversations}
                hasPrevious={hasPreviousConversations}
                loadingMore={loadingMoreConversations}
                onLoadMore={handleLoadMoreConversations}
                onLoadPrevious={handleLoadPreviousConversations}
                isMobile={isMobile}
              />
            )}

            {(!isMobile || mobileView === "chat") && (
              <ChatArea
                conv={selectedConv}
                onOpenInsights={() => setShowInsights(true)}
                onOpenForm={() => setShowFormPreview(true)}
                isMobile={isMobile}
                onConversationUpdate={handleConversationUpdate}
                leadContact={leadContact}
                assignedTicket={assignedTicket}
                team={team}
                onContactUpdated={setLeadContact}
                onTicketSaved={setAssignedTicket}
                activeChannel={activeChannel}
                initialRecipient={newConversationRecipient}
              />
            )}

            {!isMobile && (showFormPreview ? (
              <FormPreviewPanel leadContact={leadContact} onClose={() => setShowFormPreview(false)} />
            ) : (
              <InsightsPanel
                activeInsightTab={activeInsightTab}
                onTabChange={setActiveInsightTab}
                leadContact={leadContact}
                assignedTicket={assignedTicket}
                loadingLead={leadLoading}
                team={team}
                conv={selectedConv}
              />
            ))}

            {isMobile && showFormPreview && (
              <div className="fixed inset-0 bg-black/35 z-200 flex justify-end" onClick={() => setShowFormPreview(false)}>
                <div className="w-[85%] max-w-[320px] bg-white dark:bg-gray-900 h-full overflow-y-auto" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                  <FormPreviewPanel leadContact={leadContact} onClose={() => setShowFormPreview(false)} />
                </div>
              </div>
            )}

            {isMobile && showInsights && (
              <div
                className="fixed inset-0 bg-black/35 z-200 flex justify-end"
                onClick={() => setShowInsights(false)}
              >
                <div
                  className="w-[85%] max-w-[320px] bg-white dark:bg-gray-900 h-full overflow-y-auto"
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  <div className="px-4 py-3 border-b border-slate-100 dark:border-gray-800 flex justify-between">
                    <span className="font-extrabold dark:text-gray-100">ConvoAI Insights</span>
                    <button
                      onClick={() => setShowInsights(false)}
                      className="border-none bg-transparent cursor-pointer text-lg dark:text-gray-300"
                    >
                      ×
                    </button>
                  </div>
                  <InsightsPanel
                    activeInsightTab={activeInsightTab}
                    onTabChange={setActiveInsightTab}
                    leadContact={leadContact}
                    assignedTicket={assignedTicket}
                    loadingLead={leadLoading}
                    team={team}
                    conv={selectedConv}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Email Editor Dialog */}
      <EmailEditorDialog
        isOpen={isEmailEditorOpen}
        onClose={() => setIsEmailEditorOpen(false)}
        onSave={(html, design) => {
        
        }}
      />
    </div>
  );
}
